export interface ImportTemplate {
  id: string;
  competency: "reading" | "listening" | "writing";
  label: string;
  filename: string;
  description: string;
  requiresSupportImage?: boolean;
  requiresAudio?: boolean;
  requiresOptionImages?: boolean;
  template: Record<string, any>;
}

export const CE_IMPORT_TEMPLATES: ImportTemplate[] = [
  {
    id: "ce_documents_divers",
    competency: "reading",
    label: "Documents divers",
    filename: "template-ce-documents-divers.json",
    description: "Modelo para anúncios, folhetos, e-mails curtos e pequenos comunicados do cotidiano.",
    template: {
      "competency": "reading",
      "questions": [
        {
          "title": "Règlement d'espace vert",
          "section": "Documents divers",
          "instruction": "Lisez le document et répondez à la question.",
          "question": "De quel type de document s'agit-il ?",
          "body": "PARC MUNICIPAL DE LA SOURCE. Horaires d'ouverture : 08h00 - 20h00. Les chiens devem être tenus en laisse. Il est interdit de circuler à vélo dans les allées. Merci de maintenir les lieux propres.",
          "options": {
            "A": "Un règlement de parc",
            "B": "Une invitation à une fête",
            "C": "Une publicité pour un hôtel",
            "D": "Un plan de transport"
          },
          "correctAnswer": "A",
          "explanation": "La réponse correcte est A, car le document présente des règles de conduite dans un espace public.",
          "tags": ["vie quotidienne", "compréhension globale"]
        }
      ]
    }
  },
  {
    id: "ce_phrases_textes_lacunaires",
    competency: "reading",
    label: "Phrases lacunaires / Textes lacunaires",
    filename: "template-ce-phrases-textes-lacunaires.json",
    description: "Modelo para testes de lacunas. Para o simulado completo, informe 'subSection' como 'phrases_lacunaires' (4 questões) ou 'textes_lacunaires' (6 questões).",
    template: {
      "competency": "reading",
      "questions": [
        {
          "title": "Vocabulaire en contexte",
          "section": "Phrases lacunaires / Textes lacunaires",
          "metadata": {
            "subSection": "phrases_lacunaires"
          },
          "instruction": "Choisissez le mot qui complète correctement la phrase.",
          "question": "Quel mot complète le mieux le sens de cette phrase ?",
          "body": "Le directeur a (___) tous les chefs de service pour une réunion d'urgence demain matin.",
          "options": {
            "A": "convoqué",
            "B": "invité",
            "C": "interpellé",
            "D": "assemblé"
          },
          "correctAnswer": "A",
          "explanation": "La réponse correcte est A. Dans un contexte professionnel, le verbe 'convoquer' est le terme précis pour demander officiellement la présence de quelqu'un.",
          "tags": ["grammaire", "lexique"]
        }
      ]
    }
  },
  {
    id: "ce_lecture_rapide_textes_graphiques",
    competency: "reading",
    label: "Lecture rapide de textes et de graphiques",
    filename: "template-ce-lecture-rapide-textes-graphiques.json",
    description: "Modelo para leitura rápida. Para o simulado completo, informe 'subSection' como 'lecture_rapide_textes' (4 questões) ou 'lecture_rapide_graphiques' (1 questão com gráfico).",
    requiresSupportImage: true,
    template: {
      "competency": "reading",
      "questions": [
        {
          "title": "Analyse d'un graphique",
          "section": "Lecture rapide de textes et de graphiques",
          "metadata": {
            "subSection": "lecture_rapide_graphiques"
          },
          "instruction": "Observez le graphique et répondez à la question.",
          "question": "Quel mois montre le résultat le plus élevé d'après le graphique ?",
          "body": "",
          "requiresSupportImage": true,
          "supportImage": "",
          "options": {
            "A": "Janvier",
            "B": "Mars",
            "C": "Juin",
            "D": "Octobre"
          },
          "correctAnswer": "C",
          "explanation": "La réponse correcte est C, car le graphique indique que le mois de juin présente la valeur la plus élevée.",
          "tags": ["graphique", "lecture rapide"]
        }
      ]
    }
  },
  {
    id: "ce_documents_administratifs_professionnels",
    competency: "reading",
    label: "Documents administratifs et professionnels",
    filename: "template-ce-documents-administratifs-professionnels.json",
    description: "Modelo para notas de serviço, e-mails corporativos e memorandos de trabalho.",
    template: {
      "competency": "reading",
      "questions": [
        {
          "title": "Note de service",
          "section": "Documents administratifs et professionnels",
          "instruction": "Lisez le document professionnel et répondez à la question.",
          "question": "Quelle est la règle concernant les clés USB d'après cette note ?",
          "body": "NOTE DE SERVICE. Objet : Renforcement de la sécurité informatique. À partir du 1er juin, le renouvellement des mots de passe sera obligatoire tous les 30 jours. L'utilisation de clés USB personnelles est strictement interdite sur le réseau interne.",
          "options": {
            "A": "Les employés doivent changer leur mot de passe chaque semaine.",
            "B": "Les clés USB personnelles sont désormais interdites.",
            "C": "Le réseau interne sera supprimé.",
            "D": "La mesure concerne uniquement les visiteurs."
          },
          "correctAnswer": "B",
          "explanation": "La réponse correcte est B, car le document indique que les clés USB personnelles sont strictement interdites.",
          "tags": ["travail", "administratif", "sécurité"]
        }
      ]
    }
  },
  {
    id: "ce_articles_de_presse",
    competency: "reading",
    label: "Articles de presse",
    filename: "template-ce-articles-de-presse.json",
    description: "Modelo para artigos de jornal, notícias de imprensa e colunas opinativas.",
    template: {
      "competency": "reading",
      "questions": [
        {
          "title": "Analyse d'opinion",
          "section": "Articles de presse",
          "instruction": "Lisez l'article et choisissez l'affirmation correcte.",
          "question": "Quelle affirmation résume le mieux l'idée principale du texte ?",
          "body": "Depuis plusieurs années, le télétravail transforme profondément l'organisation des entreprises. Certains salariés y voient une amélioration de leur qualité de vie, tandis que d'autres regrettent la disparition des échanges spontanés au bureau. Pour les managers, le défi consiste désormais à maintenir la cohésion des équipes malgré la distance.",
          "options": {
            "A": "Le télétravail est présenté comme une solution sans difficulté.",
            "B": "Le texte évoque à la fois des avantages et des limites du télétravail.",
            "C": "L'auteur affirme que les bureaux vont disparaître complètement.",
            "D": "Le texte traite principalement des transports publics."
          },
          "correctAnswer": "B",
          "explanation": "La réponse correcte est B, car le texte présente des aspects positifs et négatifs du télétravail.",
          "tags": ["article", "opinion", "travail"]
        }
      ]
    }
  }
];

export const CO_IMPORT_TEMPLATES: ImportTemplate[] = [
  {
    id: "co_dialogues_images",
    competency: "listening",
    label: "Dialogues associés à des images",
    filename: "template-co-dialogues-images.json",
    description: "Modelo para associação áudio-imagem de diálogos cotidianos curtos. Exige áudio e 4 imagens.",
    requiresAudio: true,
    requiresOptionImages: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Conversation 1",
          "section": "Dialogues associés à des images",
          "instruction": "Écoutez le dialogue et choisissez l'image qui correspond à la situation.",
          "question": "Quelle image correspond le mieux au dialogue entendu ?",
          "requiresAudio": true,
          "requiresOptionImages": true,
          "audio": "",
          "optionAImage": "",
          "optionBImage": "",
          "optionCImage": "",
          "optionDImage": "",
          "correctAnswer": "B",
          "explanation": "La réponse correcte est B, car l'image correspond à la situation décrite dans le dialogue.",
          "tags": ["images", "dialogue"]
        }
      ]
    }
  },
  {
    id: "co_annonces_publiques",
    competency: "listening",
    label: "Annonces publiques",
    filename: "template-co-annonces-publiques.json",
    description: "Anúncios públicos gravados em aeroportos, estações, lojas ou centros urbanos. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Annonce publique",
          "section": "Annonces publiques",
          "instruction": "Écoutez l'annonce et choisissez la bonne réponse.",
          "question": "Quelle information est donnée sur le train ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "Le train est annulé.",
            "B": "Le train aura du retard.",
            "C": "Les billets sont gratuits.",
            "D": "La gare est fermée."
          },
          "correctAnswer": "B",
          "explanation": "La réponse correcte est B, car l'annonce indique un retard du train.",
          "tags": ["annonce", "transport"]
        }
      ]
    }
  },
  {
    id: "co_messages_courts_telephone",
    competency: "listening",
    label: "Messages courts / situations téléphoniques",
    filename: "template-co-messages-courts-telephone.json",
    description: "Mensagens rápidas gravadas de secretárias eletrônicas ou diálogos curtos. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Message téléphonique",
          "section": "Messages courts / situations téléphoniques",
          "instruction": "Écoutez le message et choisissez la bonne réponse.",
          "question": "Quel est l'objectif principal de cet appel ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "La personne confirme un rendez-vous.",
            "B": "La personne annule une réunion.",
            "C": "La personne demande un remboursement.",
            "D": "La personne cherche une adresse."
          },
          "correctAnswer": "A",
          "explanation": "La réponse correcte est A, car le message sert à confirmer un rendez-vous.",
          "tags": ["téléphone", "message court"]
        }
      ]
    }
  },
  {
    id: "co_micro_trottoirs",
    competency: "listening",
    label: "Micro-trottoirs",
    filename: "template-co-micro-trottoirs.json",
    description: "Modelo para pesquisas de rua de opiniões curtas sobre temas cotidianos. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Micro-trottoir",
          "section": "Micro-trottoirs",
          "instruction": "Écoutez les personnes interrogées et choisissez l'idée principale.",
          "question": "Comment décririez-vous l'opinion générale des personnes interrogées ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "Les personnes sont toutes contre le projet.",
            "B": "Les opinions exprimées sont partagées.",
            "C": "Le sujet concerne uniquement les transports.",
            "D": "Les participants refusent de répondre."
          },
          "correctAnswer": "B",
          "explanation": "La réponse correcte est B, car plusieurs points de vue différents sont exprimés.",
          "tags": ["opinion", "micro-trottoir"]
        }
      ]
    }
  },
  {
    id: "co_chroniques_radio",
    competency: "listening",
    label: "Chroniques radio",
    filename: "template-co-chroniques-radio.json",
    description: "Noticiários e colunas informativas de rádio com análise de colunistas. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Chronique radio",
          "section": "Chroniques radio",
          "instruction": "Écoutez la chronique et choisissez l'affirmation correcte.",
          "question": "Quel est le rôle principal du chroniqueur dans ce document ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "Le chroniqueur présente un fait divers local.",
            "B": "Le chroniqueur donne une analyse personnelle du sujet.",
            "C": "Le chroniqueur lit uniquement des horaires.",
            "D": "Le chroniqueur annonce une promotion commerciale."
          },
          "correctAnswer": "B",
          "explanation": "La réponse correcte est B, car une chronique radio contient souvent une analyse ou un commentaire personnel.",
          "tags": ["radio", "analyse"]
        }
      ]
    }
  },
  {
    id: "co_interviews",
    competency: "listening",
    label: "Interviews",
    filename: "template-co-interviews.json",
    description: "Modelo para entrevistas formais ou informais gravadas em programas de TV ou rádio. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Interview",
          "section": "Interviews",
          "instruction": "Écoutez l'interview et répondez à la question.",
          "question": "Quel est le sujet principal abordé par l'invité ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "L'invité présente son parcours professionnel.",
            "B": "L'invité annonce la fermeture d'un service.",
            "C": "L'invité refuse de parler de son travail.",
            "D": "L'invité donne uniquement des informations météo."
          },
          "correctAnswer": "A",
          "explanation": "La réponse correcte est A, car l'interview porte sur le parcours et l'expérience de l'invité.",
          "tags": ["interview", "parcours"]
        }
      ]
    }
  },
  {
    id: "co_reportages",
    competency: "listening",
    label: "Reportages",
    filename: "template-co-reportages.json",
    description: "Reportagens formais cobrindo fatos econômicos, científicos ou sociológicos. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Reportage",
          "section": "Reportages",
          "instruction": "Écoutez le reportage et choisissez la réponse correcte.",
          "question": "De quoi traite principalement ce reportage ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "Le reportage décrit un problème social et ses conséquences.",
            "B": "Le reportage annonce uniquement un concert.",
            "C": "Le reportage présente une recette de cuisine.",
            "D": "Le reportage donne les horaires d'un magasin."
          },
          "correctAnswer": "A",
          "explanation": "La réponse correcte est A, car le reportage présente un sujet de société avec ses effets concrets.",
          "tags": ["reportage", "société"]
        }
      ]
    }
  },
  {
    id: "co_documents_divers",
    competency: "listening",
    label: "Documents divers",
    filename: "template-co-documents-divers.json",
    description: "Qualquer outro formato de áudio ou gravação de voz contextualizada. Exige áudio.",
    requiresAudio: true,
    template: {
      "competency": "listening",
      "questions": [
        {
          "title": "Document sonore divers",
          "section": "Documents divers",
          "instruction": "Écoutez le document et choisissez la bonne réponse.",
          "question": "Quel est le but de ce document sonore ?",
          "requiresAudio": true,
          "audio": "",
          "options": {
            "A": "Le document informe le public sur une situation précise.",
            "B": "Le document présente uniquement une publicité.",
            "C": "Le document est une conversation privée sans objectif.",
            "D": "Le document ne donne aucune information utile."
          },
          "correctAnswer": "A",
          "explanation": "La réponse correcte est A, car le document sonore transmet une information précise au public.",
          "tags": ["document sonore", "compréhension"]
        }
      ]
    }
  }
];

export const EE_IMPORT_TEMPLATES: ImportTemplate[] = [
  {
    id: "ee_writing",
    competency: "writing",
    label: "Expression Écrite",
    filename: "template-ee-writing.json",
    description: "Modelo oficial estruturado contendo tarefas para a Section A (Fait divers) e Section B (Opinion).",
    template: {
      "competency": "writing",
      "questions": [
        {
          "title": "Fait divers - disparition d'un vélo",
          "section": "Section A",
          "instruction": "Complétez l'histoire en environ 80 mots.",
          "tags": ["fait divers", "rédaction"]
        },
        {
          "title": "Opinion - voyages organisés",
          "section": "Section B",
          "instruction": "Donnez votre opinion sur le sujet suivant : Les voyages organisés empêchent de découvrir réellement un pays.",
          "tags": ["argumentation", "opinion"]
        }
      ]
    }
  }
];
